// src/components/support/ChatHistory.jsx
import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    Card,
    Col,
    Empty,
    Form,
    Grid,
    InputNumber,
    List,
    Modal,
    Pagination,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    DownOutlined,
    EyeOutlined,
    FilterOutlined,
    InfoCircleOutlined,
    MessageOutlined,
    ReloadOutlined,
    UpOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";

import {useChats} from "./chat-history-query";
import {fetchOperators} from "../operators/operators-api"; // ✅ اضافه شد
import {stateMeta} from "../../../utils/chatState";
import {CHANNEL_OPTIONS, channelMeta} from "../../../utils/channel";
import VisitorJourney from "../chat/visitor-journey";

const {Text} = Typography;
const {useBreakpoint} = Grid;

/* ---------------- Time & format helpers ---------------- */
const TEHRAN_OFFSET_MINUTES = 210; // UTC+03:30, no DST

const toISOInstant = (v) => {
    if (!v) return null;
    const g = new DateObject(v).convert(gregorian);
    const y = g.year;
    const M = g.month.number;
    const d = g.day;
    const h = g.hour || 0;
    const m = g.minute || 0;
    const s = g.second || 0;
    const ms = g.millisecond || 0;

    const utcMs =
        Date.UTC(y, M - 1, d, h, m, s, ms) - TEHRAN_OFFSET_MINUTES * 60 * 1000;
    return new Date(utcMs).toISOString();
};

const startOfTehranDay = (v) =>
    new DateObject(v).set({hour: 0, minute: 0, second: 0, millisecond: 0});
const endOfTehranDay = (v) =>
    new DateObject(v).set({hour: 23, minute: 59, second: 59, millisecond: 999});

const toPersianDateObject = (d) =>
    d ? new DateObject({date: new Date(d), calendar: persian, locale: persian_fa}) : null;

const DISPLAY_FMT = "HH:mm:ss YYYY/MM/DD";
const fmtPersian = (d, token = DISPLAY_FMT) => {
    const obj = toPersianDateObject(d);
    return obj ? obj.format(token) : "-";
};

const enToFaDigits = (v) =>
    String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const rangeToInstants = (range) => {
    const [a, b] = range || [];
    const start = a ? startOfTehranDay(a) : null;
    const end = b ? endOfTehranDay(b) : null;
    return {from: toISOInstant(start), to: toISOInstant(end)};
};

const orderRange = (r) => {
    if (!r || !r[0] || !r[1]) return r;
    const aMs = r[0].toDate()?.getTime?.() ?? 0;
    const bMs = r[1].toDate()?.getTime?.() ?? 0;
    return aMs <= bMs ? r : [r[1], r[0]];
};

/* ---------------- UI meta ---------------- */

/* برچسب/رنگ وضعیت از نقشه‌ی مشترک؛ کلیدهای i18n مثل قبل حفظ شده‌اند. */
const stateColor = (st) => stateMeta(st).color;

const stateLabel = (st, t) => {
    const meta = stateMeta(st);
    return t(meta.i18nKey, meta.label);
};

/* ==================== MAIN COMPONENT ==================== */

const ChatHistory = () => {
    const {t} = useTranslation();
    const screens = useBreakpoint();
    const isDesktop = !!screens.md;
    const navigate = useNavigate();

    const [filterBoxShow, setFilterBoxShow] = useState(false);

    // ✅ مدل شبیه Operators
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});

    const [createdRange, setCreatedRange] = useState([null, null]);
    const [updatedRange, setUpdatedRange] = useState([null, null]);

    const [filters, setFilters] = useState({
        state: "ALL",
        channel: "ALL",
    });

    const [form] = Form.useForm();

    const createdInstants = useMemo(
        () => rangeToInstants(createdRange),
        [createdRange]
    );
    const updatedInstants = useMemo(
        () => rangeToInstants(updatedRange),
        [updatedRange]
    );

    const [operatorOptions, setOperatorOptions] = useState([]);
    const [operatorLoading, setOperatorLoading] = useState(false);

    const loadOperators = async (searchText = "") => {
        setOperatorLoading(true);
        try {
            const filters = searchText ? {fullName: searchText} : {};
            const res = await fetchOperators(0, 10, filters);
            const list = res?.content || res?.data?.content || [];
            setOperatorOptions(list);
        } catch (e) {
            console.error("Error loading operators for chat filter:", e);
        } finally {
            setOperatorLoading(false);
        }
    };

    // بار اول ۱۰ اپراتور اول بدون فیلتر
    useEffect(() => {
        loadOperators("");
    }, []);

    // فیلترهای ارسالی به بک‌اند
    const apiFilters = useMemo(
        () => ({
            operatorId: filters.operatorId || undefined, // ✅ اینجا id اپراتور می‌رود
            state: filters.state === "ALL" ? undefined : filters.state,
            // "ALL" is a UI-only sentinel; the backend filter is simply absent when it is set.
            channel: filters.channel === "ALL" ? undefined : filters.channel,
            minScore: filters.minScore ?? undefined,
            maxScore: filters.maxScore ?? undefined,
            fromCreatedAt: createdInstants.from || undefined,
            toCreatedAt: createdInstants.to || undefined,
            fromUpdatedAt: updatedInstants.from || undefined,
            toUpdatedAt: updatedInstants.to || undefined,
        }),
        [filters, createdInstants, updatedInstants]
    );

    // گرفتن دیتا از بک‌اند (page = 0-based)
    const {data, isLoading, refetch} = useChats(
        pagination.current - 1,
        pagination.pageSize,
        apiFilters
    );

    const rows = useMemo(
        () => data?.data?.content || data?.content || [],
        [data]
    );

    const total =
        data?.data?.totalElements ??
        data?.totalElements ??
        rows.length;

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);

    const openDetails = (row) => {
        setDetailRecord(row);
        setDetailOpen(true);
    };

    const goToChat = (row) => {
        navigate(`/dashboard/chats/${row.id}`, {
            state: {chat: row},
        });
    };

    /* --------- columns (desktop) --------- */
    const columns = useMemo(
        () => [
            {
                title: t("ch.col_index", "ردیف"),
                key: "index",
                align: "center",
                width: 64,
                render: (_text, _rec, idx) =>
                    enToFaDigits(
                        (pagination.current - 1) * pagination.pageSize + idx + 1
                    ),
            },
            {
                title: t("ch.col_fullName", "نام کاربر"),
                dataIndex: "fullName",
                key: "fullName",
                width: 160,
                ellipsis: true,
                render: (v) => v || "-",
            },
            {
                // Where the conversation came from. First thing an operator reading history needs:
                // a Telegram thread and a web-chat session are answered, escalated and followed up
                // differently, and until now the two were indistinguishable in this table.
                title: t("ch.col_channel", "منبع"),
                dataIndex: "channel",
                key: "channel",
                align: "center",
                width: 150,
                render: (v) => {
                    const meta = channelMeta(v, t);
                    return (
                        <Tag color={meta.color} style={{borderRadius: 999}}>
                            {meta.icon} {meta.label}
                        </Tag>
                    );
                },
            },
            {
                title: t("ch.col_state", "وضعیت"),
                dataIndex: "state",
                key: "state",
                align: "center",
                width: 140,
                render: (v) => (
                    <Tag color={stateColor(v)} style={{borderRadius: 999}}>
                        {stateLabel(v, t)}
                    </Tag>
                ),
            },
            {
                title: t("ch.col_waitingTime", "زمان انتظار (دقیقه)"),
                dataIndex: "waitingTime",
                key: "waitingTime",
                align: "center",
                width: 130,
                render: (v) => {
                    if (v == null) return "-";
                    const minutes = Math.round((v / 60) * 100) / 100; // دو رقم اعشار
                    return enToFaDigits(minutes);
                },
            },
            {
                title: t("ch.col_operator", "اپراتور"),
                dataIndex: "operatorName",
                key: "operatorName",
                align: "center",
                width: 160,
                render: (v) => v || "-",
            },
            {
                title: t("ch.col_score", "امتیاز"),
                dataIndex: "score",
                key: "score",
                align: "center",
                width: 90,
                render: (v) => (v != null ? enToFaDigits(v) : "-"),
            },
            {
                title: t("ch.col_createdAt", "ایجاد"),
                dataIndex: "createdAt",
                key: "createdAt",
                align: "center",
                width: 170,
                render: (v) => fmtPersian(v, "HH:mm YYYY/MM/DD"),
            },
            {
                title: t("ch.col_updatedAt", "آخرین تغییر"),
                dataIndex: "updatedAt",
                key: "updatedAt",
                align: "center",
                width: 170,
                render: (v) => fmtPersian(v, "HH:mm YYYY/MM/DD"),
            },
            {
                title: t("ch.col_summary", "خلاصه چت"),
                dataIndex: "summary",
                key: "summary",
                width: 260,
                ellipsis: {showTitle: false},
                render: (v) => (
                    <Tooltip title={v || ""}>
                        <span>{v || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                title: t("ch.col_lastMessage", "آخرین پیام"),
                dataIndex: "lastMessage",
                key: "lastMessage",
                width: 260,
                ellipsis: {showTitle: false},
                render: (v) => (
                    <Tooltip title={v || ""}>
                        <span>{v || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                title: t("ch.actions", "عملیات"),
                key: "actions",
                fixed: "right",
                align: "center",
                width: 150,
                render: (_text, r) => (
                    <Space>
                        <Tooltip title={t("ch.open_chat", "باز کردن چت")}>
                            <Button
                                size="small"
                                type="primary"
                                icon={<MessageOutlined/>}
                                onClick={() => goToChat(r)}
                            />
                        </Tooltip>
                        <Tooltip title={t("ch.details", "جزئیات")}>
                            <Button
                                size="small"
                                icon={<EyeOutlined/>}
                                onClick={() => openDetails(r)}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
        ],
        [t, pagination.current, pagination.pageSize]
    );

    /* --------- Mobile card ---------- */
    const renderMobileCard = (r, idxInPage) => (
        <Card
            key={r.id}
            size="small"
            className="chat-mobile-card"
            bodyStyle={{padding: 12}}
            onDoubleClick={() => openDetails(r)}
        >
            <div style={{display: "flex", justifyContent: "space-between", gap: 8}}>
                <Space>
                    <UserOutlined/>
                    <Text strong>{r.fullName || "-"}</Text>
                </Space>
                <Tag color={stateColor(r.state)}>
                    {stateLabel(r.state, t)}
                </Tag>
            </div>

            <div style={{marginTop: 6}}>
                <Tag color={channelMeta(r.channel, t).color} style={{borderRadius: 999}}>
                    {channelMeta(r.channel, t).icon} {channelMeta(r.channel, t).label}
                </Tag>
            </div>

            <div style={{marginTop: 6, display: "flex", justifyContent: "space-between", gap: 8}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("ch.col_index", "ردیف")}:
                </Text>
                <Text>
                    {enToFaDigits(
                        (pagination.current - 1) * pagination.pageSize + idxInPage + 1
                    )}
                </Text>
            </div>

            <div style={{marginTop: 4, display: "flex", justifyContent: "space-between", gap: 8}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("ch.col_operator", "اپراتور")}
                </Text>
                <Text>{r.operatorName || "-"}</Text>
            </div>

            <div style={{marginTop: 4, display: "flex", justifyContent: "space-between", gap: 8}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("ch.col_score", "امتیاز")}
                </Text>
                <Text>{r.score != null ? enToFaDigits(r.score) : "-"}</Text>
            </div>

            <div style={{marginTop: 4}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("ch.col_summary", "خلاصه چت")}
                </Text>
                <div style={{fontSize: 13}}>{r.summary || "-"}</div>
            </div>

            <div style={{marginTop: 4}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("ch.col_lastMessage", "آخرین پیام")}
                </Text>
                <div style={{fontSize: 13}}>{r.lastMessage || "-"}</div>
            </div>

            <div style={{marginTop: 4, display: "flex", justifyContent: "space-between"}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("ch.col_waitingTime", "زمان انتظار (دقیقه)")}
                </Text>
                <Text>
                    {r.waitingTime != null
                        ? enToFaDigits(Math.round((r.waitingTime / 60) * 100) / 100)
                        : "-"}
                </Text>
            </div>

            <div style={{marginTop: 4, display: "grid", rowGap: 2}}>
                <div style={{display: "flex", justifyContent: "space-between", gap: 8}}>
                    <Text type="secondary" style={{fontSize: 12}}>
                        {t("ch.col_createdAt", "ایجاد")}
                    </Text>
                    <Text>{fmtPersian(r.createdAt, "HH:mm YYYY/MM/DD")}</Text>
                </div>
                <div style={{display: "flex", justifyContent: "space-between", gap: 8}}>
                    <Text type="secondary" style={{fontSize: 12}}>
                        {t("ch.col_updatedAt", "آخرین تغییر")}
                    </Text>
                    <Text>{fmtPersian(r.updatedAt, "HH:mm YYYY/MM/DD")}</Text>
                </div>
            </div>

            <Space style={{marginTop: 8}} wrap>
                <Button
                    size="small"
                    type="primary"
                    icon={<MessageOutlined/>}
                    onClick={() => goToChat(r)}
                >
                    {t("ch.open_chat", "باز کردن چت")}
                </Button>
                <Button
                    size="small"
                    icon={<EyeOutlined/>}
                    onClick={() => openDetails(r)}
                >
                    {t("ch.details", "جزئیات")}
                </Button>
            </Space>
        </Card>
    );

    return (
        <>
            <Card
                // ✅ عنوان فقط روی دسکتاپ
                title={
                    isDesktop ? (
                        <Space>
                            <MessageOutlined/>
                            <span>{t("ch.title", "تاریخچه چت‌ها")}</span>
                        </Space>
                    ) : null
                }
                extra={
                    <Space size="small" wrap>
                        <Tooltip title={t("ch.filter", "فیلتر")}>
                            <Button
                                icon={filterBoxShow ? <UpOutlined/> : <DownOutlined/>}
                                onClick={() => setFilterBoxShow((s) => !s)}
                            >
                                {t("ch.filter", "فیلتر")}
                            </Button>
                        </Tooltip>
                        <Tooltip title={t("ch.reload", "بارگذاری مجدد")}>
                            <Button
                                icon={<ReloadOutlined/>}
                                onClick={() => {
                                    refetch();
                                }}
                            >
                                {t("ch.reload", "بارگذاری مجدد")}
                            </Button>
                        </Tooltip>
                    </Space>
                }
                bodyStyle={{padding: 12}}
            >
                {/* فیلترها */}
                {filterBoxShow && (
                    <Card size="small" style={{marginBottom: 12}}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={(vals) => {
                                setFilters({
                                    operatorId: vals.operatorId || undefined, // ✅ اینجا id اپراتور
                                    state: vals.state || "ALL",
                                    channel: vals.channel || "ALL",
                                    minScore: vals.minScore ?? null,
                                    maxScore: vals.maxScore ?? null,
                                });
                                setPagination((prev) => ({
                                    ...prev,
                                    current: 1,
                                }));
                            }}
                        >
                            <Row gutter={[16, 8]}>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="operatorId"
                                        label={t(
                                            "ch.filter_operator",
                                            "اپراتور (جستجو بر اساس نام)"
                                        )}
                                    >
                                        <Select
                                            allowClear
                                            showSearch
                                            placeholder={t(
                                                "ch.filter_operator_placeholder",
                                                "نام کامل اپراتور را جستجو کنید"
                                            )}
                                            filterOption={false}
                                            loading={operatorLoading}
                                            onSearch={(value) => loadOperators(value)}
                                            onFocus={() => {
                                                if (!operatorOptions.length) {
                                                    loadOperators("");
                                                }
                                            }}
                                            notFoundContent={t(
                                                "ch.operator_not_found",
                                                "اپراتوری با این نام یافت نشد"
                                            )}
                                            options={operatorOptions.map((op) => ({
                                                value: op.id, // ✅ id می‌رود برای فیلتر
                                                label: op.fullName || op.email || op.id,
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="state"
                                        label={t("ch.filter_state", "وضعیت")}
                                        initialValue="ALL"
                                    >
                                        <Select
                                            allowClear
                                            options={[
                                                {value: "ALL", label: t("ch.states.all", "همه")},
                                                {value: "AGENT", label: stateLabel("AGENT", t)},
                                                {value: "WAITING", label: stateLabel("WAITING", t)},
                                                {value: "OPERATOR", label: stateLabel("OPERATOR", t)},
                                                {
                                                    value: "AGENT_FINISHED",
                                                    label: stateLabel("AGENT_FINISHED", t),
                                                },
                                                {
                                                    value: "OPERATOR_FINISHED",
                                                    label: stateLabel("OPERATOR_FINISHED", t),
                                                },
                                                {value: "TIME_OUT", label: stateLabel("TIME_OUT", t)},
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="channel"
                                        label={t("ch.filter_channel", "منبع")}
                                        initialValue="ALL"
                                    >
                                        <Select
                                            allowClear
                                            options={[
                                                {value: "ALL", label: t("ch.channels.all", "همه")},
                                                ...CHANNEL_OPTIONS(t),
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="minScore" label={t("ch.minScore", "حداقل امتیاز")}>
                                        <InputNumber min={0} max={5} style={{width: "100%"}}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="maxScore" label={t("ch.maxScore", "حداکثر امتیاز")}>
                                        <InputNumber min={0} max={5} style={{width: "100%"}}/>
                                    </Form.Item>
                                </Col>

                                {/* بازه ایجاد */}
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label={t("ch.created_range", "بازه ایجاد")}>
                                        <DatePicker
                                            range
                                            value={createdRange}
                                            onChange={(v) => setCreatedRange(orderRange(v))}
                                            calendar={persian}
                                            locale={persian_fa}
                                            format="YYYY/MM/DD"
                                            style={{width: "100%"}}
                                            calendarPosition="bottom-right"
                                        />
                                    </Form.Item>
                                </Col>

                                {/* بازه آپدیت */}
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label={t("ch.updated_range", "بازه آخرین تغییر")}>
                                        <DatePicker
                                            range
                                            value={updatedRange}
                                            onChange={(v) => setUpdatedRange(orderRange(v))}
                                            calendar={persian}
                                            locale={persian_fa}
                                            format="YYYY/MM/DD"
                                            style={{width: "100%"}}
                                            calendarPosition="bottom-right"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row justify="center">
                                <Col>
                                    <Space>
                                        <Button type="primary" htmlType="submit" icon={<FilterOutlined/>}>
                                            {t("ch.filter", "فیلتر")}
                                        </Button>
                                        <Button
                                            icon={<ReloadOutlined/>}
                                            onClick={() => {
                                                form.resetFields();
                                                setCreatedRange([null, null]);
                                                setUpdatedRange([null, null]);
                                                setFilters({state: "ALL", channel: "ALL"});
                                                setPagination({current: 1, pageSize: 10});
                                                loadOperators("");
                                            }}
                                        >
                                            {t("ch.reset", "ریست")}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                )}

                {/* جدول دسکتاپ / کارت موبایل */}
                {isDesktop ? (
                    <Table
                        rowKey="id"
                        size="small"
                        bordered
                        dataSource={rows}
                        columns={columns}
                        loading={isLoading}
                        pagination={false} // ✅ مثل Operators
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t("ch.no_data", "چتی یافت نشد")}
                                />
                            ),
                        }}
                        scroll={{x: 1100}}
                        rowClassName={(_, idx) => (idx % 2 === 0 ? "chat-zebra-row" : "")}
                        onRow={(record) => ({
                            onDoubleClick: () => openDetails(record),
                        })}
                    />
                ) : (
                    <>
                        <List
                            dataSource={rows}
                            split={false}
                            className="chat-mobile-list"
                            locale={{emptyText: t("ch.no_data", "چتی یافت نشد")}}
                            renderItem={(item, idx) => (
                                <List.Item style={{padding: 0, border: "none"}}>
                                    {renderMobileCard(item, idx)}
                                </List.Item>
                            )}
                            style={{marginBottom: 12}}
                        />
                    </>
                )}

                {/* ✅ پیجینیشن مشترک مثل Operators */}
                {total > 0 && (
                    <div
                        style={{
                            marginTop: 12,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Pagination
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={total}
                            showSizeChanger
                            pageSizeOptions={["5", "10", "20", "50"]}
                            locale={{items_per_page: ""}}
                            onChange={(current, pageSize) =>
                                setPagination({current, pageSize})
                            }
                        />
                    </div>
                )}

                <style>{`
          /* Zebra striping must follow the theme — a fixed light grey stayed white in dark
             mode and made the row text unreadable. colorFillAlter is AntD's own subtle
             surface tint and is defined for both algorithms. */
          .chat-zebra-row td { background: var(--npe-row-alt); }

          @media (max-width: 767.99px) {
            .chat-mobile-list .ant-list-items {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            .chat-mobile-card {
              width: 100%;
              border-radius: 10px;
            }
          }
        `}</style>
            </Card>

            {/* جزئیات چت */}
            <Modal
                open={detailOpen}
                onCancel={() => setDetailOpen(false)}
                onOk={() => setDetailOpen(false)}
                title={
                    <Space>
                        <InfoCircleOutlined/>
                        <span>{t("ch.details_title", "جزئیات چت")}</span>
                    </Space>
                }
                okText={t("ch.close", "بستن")}
                cancelButtonProps={{style: {display: "none"}}}
                width={720}
            >
                {detailRecord && (
                    <div style={{display: "grid", rowGap: 8}}>
                        <Space>
                            <UserOutlined/>
                            <Text strong>{detailRecord.fullName || "-"}</Text>
                        </Space>
                        <div>
                            <Text type="secondary">{t("ch.col_channel", "منبع")}: </Text>
                            <Tag color={channelMeta(detailRecord.channel, t).color} style={{borderRadius: 999}}>
                                {channelMeta(detailRecord.channel, t).icon}{" "}
                                {channelMeta(detailRecord.channel, t).label}
                            </Tag>
                        </div>
                        {/* Only Telegram chats have one. A web chat's chatId is NULL by design — that
                            NULL is the fence between the two channels — so the row used to render as
                            an empty "چت آیدی تلگرام:" on every web conversation. */}
                        {detailRecord.channel !== "WEB" && detailRecord.chatId != null && (
                            <div>
                                <Text type="secondary">چت آیدی تلگرام: </Text>
                                <Text>{enToFaDigits(detailRecord.chatId)}</Text>
                            </div>
                        )}
                        <div>
                            <Text type="secondary">وضعیت: </Text>
                            <Tag color={stateColor(detailRecord.state)}>
                                {stateLabel(detailRecord.state, t)}
                            </Tag>
                        </div>
                        <div>
                            <Text type="secondary">اپراتور: </Text>
                            <Text>{detailRecord.operatorName || "-"}</Text>
                        </div>
                        <div>
                            <Text type="secondary">امتیاز: </Text>
                            <Text>
                                {detailRecord.score != null ? enToFaDigits(detailRecord.score) : "-"}
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary">ایجاد: </Text>
                            <Text>{fmtPersian(detailRecord.createdAt, "HH:mm YYYY/MM/DD")}</Text>
                        </div>
                        <div>
                            <Text type="secondary">آخرین تغییر: </Text>
                            <Text>{fmtPersian(detailRecord.updatedAt, "HH:mm YYYY/MM/DD")}</Text>
                        </div>
                        <div>
                            <Text type="secondary">آخرین پیام: </Text>
                            <div>{detailRecord.lastMessage || "-"}</div>
                        </div>
                        <div>
                            <Text type="secondary">زمان انتظار (دقیقه): </Text>
                            <Text>
                                {detailRecord.waitingTime != null
                                    ? enToFaDigits(Math.round((detailRecord.waitingTime / 60) * 100) / 100)
                                    : "-"}
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary">خلاصه چت: </Text>
                            <div>{detailRecord.summary || "-"}</div>
                        </div>
                        {/* Renders nothing at all for a Telegram chat — the component returns null on
                            any non-WEB channel, so there is no branch to keep in sync here. */}
                        {detailRecord.channel === "WEB" && (
                            <div style={{marginTop: 4}}>
                                <Text type="secondary">{t("journey.title", "مسیر بازدیدکننده در سایت")}</Text>
                                <div style={{marginTop: 8}}>
                                    <VisitorJourney
                                        chatId={detailRecord.id}
                                        channel={detailRecord.channel}
                                        live={false}
                                        compact
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </>
    );
};

export default ChatHistory;
